import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

// Constants for scoring and calculations
export const CONSTANTS = {
  // Standard score constants
  MAX_STANDARD_SCORE: 150,
  MIN_STANDARD_SCORE: 0,
  AVERAGE_RANGE_MIN: 85,
  AVERAGE_RANGE_MAX: 115,
  ADVANCED_MIN: 116,
  VERY_ADVANCED_MIN: 130,
  DELAYED_MAX: 84,
  VERY_DELAYED_MAX: 69,
  
  // Age calculation constants
  MONTHS_PER_YEAR: 12,
  MAX_AGE_MONTHS: 84, // 7 years
  
  // Pagination constants
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
  
  // Assessment intervals (in months)
  ASSESSMENT_INTERVAL_MONTHS: 3,
  DUE_SOON_DAYS: 14,
} as const;

/**
 * Combines multiple class values into a single className string using tailwind-merge
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: Date | string): string {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Calculates age in months given a date of birth and optional test date
 * @param dateOfBirth - The date of birth
 * @param testDate - The date to calculate age at (defaults to current date)
 * @returns Age in months, minimum 0
 */
export function calculateAgeInMonths(dateOfBirth: Date, testDate: Date = new Date()): number {
  if (!(dateOfBirth instanceof Date) || Number.isNaN(dateOfBirth.getTime())) {
    return 0;
  }
  if (!(testDate instanceof Date) || Number.isNaN(testDate.getTime())) {
    testDate = new Date();
  }
  
  const years = testDate.getFullYear() - dateOfBirth.getFullYear();
  const months = testDate.getMonth() - dateOfBirth.getMonth();
  let ageMonths = years * CONSTANTS.MONTHS_PER_YEAR + months;

  if (testDate.getDate() < dateOfBirth.getDate()) {
    ageMonths -= 1;
  }

  return Math.max(0, ageMonths);
}

/**
 * Formats age in months into a human-readable string
 * @param ageInMonths - Age in months
 * @returns Formatted age string (e.g., "2y 3m" or "5 months")
 */
export function formatAge(ageInMonths: number): string {
  if (typeof ageInMonths !== 'number' || Number.isNaN(ageInMonths) || ageInMonths < 0) {
    return 'N/A';
  }
  
  const years = Math.floor(ageInMonths / CONSTANTS.MONTHS_PER_YEAR);
  const months = ageInMonths % CONSTANTS.MONTHS_PER_YEAR;
  
  if (years === 0) {
    return `${months} month${months !== 1 ? 's' : ''}`;
  }
  
  return `${years}y ${months}m`;
}

/**
 * Extracts initials from a full name
 * @param name - Full name to extract initials from
 * @returns Up to 2 uppercase initials
 */
export function getInitials(name: string): string {
  if (!name || typeof name !== 'string') {
    return '?';
  }
  
  return name
    .split(' ')
    .filter(n => n.length > 0)
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Returns the proper ordinal suffix for a number (1st, 2nd, 3rd, 4th, etc.)
 * @param n - The number to get the ordinal suffix for
 * @returns The number with its ordinal suffix
 */
export function getOrdinalSuffix(n: number): string {
  if (Number.isNaN(n) || n === null || n === undefined) return 'N/A';
  const num = Math.floor(n);
  const lastDigit = num % 10;
  const lastTwoDigits = num % 100;
  
  // Handle teens (11th, 12th, 13th)
  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return `${num}th`;
  }
  
  switch (lastDigit) {
    case 1: return `${num}st`;
    case 2: return `${num}nd`;
    case 3: return `${num}rd`;
    default: return `${num}th`;
  }
}
